import type { useSWRConfig } from 'swr';

import { DEFECT_ANALYTICS_SWR_KEY } from '~/components/compliance/useDefectAnalytics';
import { getCertificateMutator } from '~/data/hooks/utils';

/**
 * The cache-scoped `mutate` handed back by `useSWRConfig()`.
 *
 * Every caller must pass this one and not the module-level `mutate` exported
 * from `swr`: the module-level mutator is bound to the default cache, so under
 * an `SWRConfig` that supplies its own provider it silently does nothing.
 */
type ScopedMutate = ReturnType<typeof useSWRConfig>['mutate'];

/**
 * Invalidate every SWR key on the compliance screen that displays defect state.
 *
 * Each of these surfaces sits on its own key with `revalidateIfStale`,
 * `revalidateOnFocus` and `revalidateOnReconnect` all off and a five-minute
 * deduping window, so nothing refetches on its own. A handler that mutates only
 * the key its own component reads leaves the rest of the screen showing
 * pre-change numbers until a full page reload — the server said 6 while the
 * screen said 7. Any mutation that adds, edits or resolves a defect has to go
 * through here.
 *
 *   ['violation', carId]          panel defect list, its open-count chip and
 *                                 the panel's "Open Defects" figure
 *   ['certificate', carId]        the car's compliance documents (via the
 *                                 mutator predicate)
 *   ['compliance', params]        grid rows plus the summary tiles' metadata
 *   ['compliance', 'car', carId]  the panel record, when the panel is mounted
 *                                 from ComplianceDrawerFlow
 *   [DEFECT_ANALYTICS_SWR_KEY, …] severity donut, open-vs-closed and the
 *                                 upcoming-due histogram
 *
 * The two `compliance` shapes share a prefix, so one predicate covers both.
 * Locale-agnostic on purpose: these are the same keys on the US (ASME) screen,
 * and the analytics key simply does not exist there because `useDefectAnalytics`
 * passes a null key when the panel is disabled. (CX-416)
 */
export const revalidateComplianceScreen = (mutate: ScopedMutate, carId: string | undefined) =>
  Promise.all([
    mutate(['violation', carId]),
    mutate(getCertificateMutator(carId)),
    mutate((key) => Array.isArray(key) && key[0] === 'compliance', undefined, {
      revalidate: true,
    }),
    mutate((key) => Array.isArray(key) && key[0] === DEFECT_ANALYTICS_SWR_KEY, undefined, {
      revalidate: true,
    }),
  ]);
